<template>
  <div
    class="outfit-container"
    :class="{ selected: isSelectedOutfit }"
    v-on:click.prevent.stop="setOutfit(outfit)"
  >
    <div class="parent">
      <div class="outfit-title text-truncate">
        <span class="text-truncate" :alt="outfitTitle">{{ outfitTitle }}</span>
      </div>

      <div class="stitch-heart">
        <StitchButton
          :capsule-id="outfit.capsule_id"
          :outfit-id="outfit.id"
          :outfit-title="outfitTitle"
          :current-user-stitched-outfit="currentUserStitchedOutfit"
        />
      </div>

      <div class="copy-snippet" v-show="false">
        <b-button
          variant="link"
          v-on:click.prevent.stop=""
          v-clipboard="generateCodeSnippet()"
          v-clipboard:success="clipboardSuccessHandler"
          v-clipboard:error="clipboardErrorHandler"
        >
          <i :class="clipboardIcon"></i>
        </b-button>
      </div>

      <div class="stitch-count">
        <div class="stitches-text text-dark">
          <span v-html="stitchesText"></span>
        </div>
      </div>

      <div class="product-thumbnails">
        <div class="capsule-thumbnail">
          <div class="outfit-row top-row">
            <img
              v-for="product in topRow"
              class="outfit-product"
              :key="product.id"
              :src="product.featured_image"
            />
          </div>
          <div class="outfit-row bottom-row">
            <img
              v-for="product in bottomRow"
              class="outfit-product"
              :key="product.id"
              :src="product.featured_image"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StitchButton from "@/components/showCapsule/StitchButton";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    StitchButton,
  },

  mounted() {
    this.topRow = this.sortProducts(["top", "bottom"]);
    this.bottomRow = this.sortProducts(["footwear", "accessory"]);
  },

  data() {
    return {
      topRow: [],
      bottomRow: [],
      clipboardSuccess: false,
    };
  },

  props: {
    outfit: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(["selectedOutfit"]),

    outfitTitle() {
      return this.outfit.user_title == null
        ? this.outfit.title
        : this.outfit.user_title;
    },

    currentUserStitchedOutfit() {
      return this.outfit.user_title !== null;
    },

    stitchesText() {
      const count = this.outfit.stitches_count;

      return `<span class="font-weight-bold">${count}</span> ${this.pluralizeStitch(
        count
      )}`;
    },

    clipboardIcon() {
      return this.clipboardSuccess ? "pe-7s-check" : "pe-7s-note2";
    },

    isSelectedOutfit() {
      return this.selectedOutfit.outfitId === this.outfit.id;
    },
  },

  methods: {
    ...mapActions(["checkAndSetOutfit"]),

    sortProducts(includesList) {
      const _ = this.$lodash;

      const productObjs = _.filter(this.outfit.products, function (product) {
        return _.includes(includesList, product.group);
      });

      return _.sortBy(productObjs, "sequence");
    },

    setOutfit(outfit) {
      this.checkAndSetOutfit({ outfitId: outfit.id }).then(() => {
        this.$parent.$parent.$parent.$refs.outfitTitleBar.scrollIntoView();
      });
    },

    pluralizeStitch(count) {
      return count === 1 ? "stitch" : "stitches";
    },

    generateCodeSnippet() {
      const generatedUrl = `https://api.unstitched.xyz/outfits/${this.outfit.id}/iframe`;
      const codeSnippet = `<!-- unstitched.xyz outfit -->
  \u003cscript src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/3.6.1/iframeResizer.js"\u003e\u003c/script\u003e
  <div class="unstitched__iframe-wrapper" style="padding-top: 30px;">
    <iframe id="unstitched__iframe_outfit_${this.outfit.id}" src="${generatedUrl}" width="100%" frameBorder="0" scrolling="no" onload="iFrameResize({checkOrigin: false}, '#unstitched__iframe_outfit_${this.outfit.id}')">
      <p>Your browser does not support iframes. Click <a href="${generatedUrl}">here</a> to see this capsule.</p>
    </iframe>
  </div>
<!-- END unstitched.xyz outfit -->`;
      return codeSnippet;
    },

    clipboardSuccessHandler({ value, event }) {
      this.clipboardSuccess = true;
      setTimeout(() => (this.clipboardSuccess = false), 1000);
    },

    clipboardErrorHandler({ value, event }) {
      console.log("error", value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";

.outfit-title {
  text-align: left;
  text-transform: capitalize;
}

.stitches-text {
  font-size: 10px;
  margin-bottom: 25px;
}

.outfit-container {
  border: 1px solid #ced4da;
  background-color: $grey8;
  min-width: 250px;
  padding: 15px;
  cursor: pointer;

  &:hover {
    border: 1px solid #c5c5c5;
  }

  &.selected {
    border-color: $primary-accent;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.05);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(.selected) {
      border-color: $mint-bright;
      box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.08);
    }
  }

  .parent {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(1, 1fr);
    grid-column-gap: 0px;
    grid-row-gap: 0px;
  }

  .outfit-title {
    grid-area: 1 / 1 / 2 / 5;
    padding-right: 5px;
  }

  .stitch-heart {
    grid-area: 1 / 5 / 2 / 6;
  }

  .copy-snippet {
    text-align: center;
    grid-area: 2 / 5 / 3 / 6;
    margin-top: 0px;
    margin-bottom: 10px;

    .btn {
      padding: 0;

      i {
        font-size: 23px;
        color: #a9a9a9;
        margin-top: 5px;

        &.pe-7s-check {
          color: $emerald !important;
        }
        &:hover {
          color: $primary-blue;
        }
      }
    }
  }

  .stitch-count {
    grid-area: 2 / 1 / 3 / 5;
  }

  .product-thumbnails {
    grid-area: 3 / 1 / 4 / 6;
  }
}

.capsule-thumbnail {
  background-color: $grey8;
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  flex-flow: row wrap;

  .top-row {
    margin-bottom: 10px;
  }

  .outfit-row {
    width: 100%;
    display: flex;
    justify-content: center;

    img.outfit-product {
      padding: 3px;
      margin-left: 15px;
      width: 45px;
      height: 50px;
      background-color: #fff;
      border-radius: 0.3em !important;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/showCapsule/Outfit.vue